export interface RnUzurkuvanjeUserRef {
  _id: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
}

export type RnUzurkuvanjePriority = 'high' | 'medium' | 'low';
export type RnUzurkuvanjeStatus = 'open' | 'answered' | 'closed' | 'approved';
export type RnUzurkuvanjeMessageKind = 'request' | 'response';

export interface RnUzurkuvanjeQcApproval {
  approvedBy?: RnUzurkuvanjeUserRef | string | null;
  approvedByName?: string;
  approvedAt?: string | null;
}

export interface RnUzurkuvanjeQcRelease {
  decision: 'approved' | 'rejected' | null;
  analysisNumber: string;
  inSpec: boolean | null;
  comment: string;
  decidedBy?: RnUzurkuvanjeUserRef | string | null;
  decidedByName: string;
  decidedAt: string | null;
}

export interface RnUzurkuvanjeReleasePayload {
  decision: 'approved' | 'rejected';
  analysisNumber: string;
  inSpec: boolean;
  comment?: string;
  pin: string;
}

export interface RnUzurkuvanjeRecipient {
  userId: RnUzurkuvanjeUserRef | string;
  username?: string;
}

export interface RnUzurkuvanjeMessage {
  _id: string;
  author?: RnUzurkuvanjeUserRef | string | null;
  authorName?: string;
  kind: RnUzurkuvanjeMessageKind;
  text: string;
  createdAt: string;
}

export interface RnUzurkuvanjeThread {
  _id: string;
  number?: string;
  rnWorkOrder: string;
  subject: string;
  priority: RnUzurkuvanjePriority;
  status: RnUzurkuvanjeStatus;
  dateTo?: string | null;
  recipients: RnUzurkuvanjeRecipient[];
  messages: RnUzurkuvanjeMessage[];
  createdBy?: RnUzurkuvanjeUserRef | string | null;
  createdAt: string;
  updatedAt: string;
  qcApproval?: RnUzurkuvanjeQcApproval;
  qcRelease?: RnUzurkuvanjeQcRelease;
  rows?: Array<{ subprocessId: string; rowKey: string; ident?: string; name?: string; quantity?: number }>;
}

export interface CreateRnUzurkuvanjeThreadPayload {
  rnWorkOrder: string;
  subject: string;
  priority: RnUzurkuvanjePriority;
  recipients: string[];
  rows?: Array<{ subprocessId: string; rowKey: string }>;
  text: string;
  dateTo?: string | null;
}

export interface RnUzurkuvanjeThreadListResponse {
  data: RnUzurkuvanjeThread[];
  page: number;
  totalPages: number;
  total: number;
  nextNumber?: string;
  sampledSubprocessIds: string[];
  viewerCanApproveAsQc: boolean;
  intermediateRequiresQcRelease: boolean;
  viewerCanReleaseAsQc: boolean;
}

export const normalizeUzurkuvanjeListResponse = (payload: unknown): RnUzurkuvanjeThreadListResponse => {
  if (Array.isArray(payload)) {
    return { data: payload as RnUzurkuvanjeThread[], page: 1, totalPages: 1, total: payload.length, sampledSubprocessIds: [], viewerCanApproveAsQc: false, intermediateRequiresQcRelease: false, viewerCanReleaseAsQc: false };
  }
  const envelope = (payload || {}) as Partial<RnUzurkuvanjeThreadListResponse> & {
    rows?: RnUzurkuvanjeThread[];
    pagination?: { page?: number; total?: number; pages?: number };
  };
  const data = Array.isArray(envelope.data) ? envelope.data : Array.isArray(envelope.rows) ? envelope.rows : [];
  const pagination = envelope.pagination;
  return {
    data,
    page: Number(pagination?.page ?? envelope.page) || 1,
    totalPages: Number(pagination?.pages ?? envelope.totalPages) || 1,
    total: Number(pagination?.total ?? envelope.total) || data.length,
    nextNumber: typeof envelope.nextNumber === 'string' ? envelope.nextNumber : '',
    sampledSubprocessIds: Array.isArray(envelope.sampledSubprocessIds) ? envelope.sampledSubprocessIds : [],
    viewerCanApproveAsQc: envelope.viewerCanApproveAsQc === true,
    intermediateRequiresQcRelease: envelope.intermediateRequiresQcRelease === true,
    viewerCanReleaseAsQc: envelope.viewerCanReleaseAsQc === true,
  };
};

export const normalizeUzurkuvanjeResponse = (payload: unknown): RnUzurkuvanjeThread | null => {
  if (!payload || typeof payload !== 'object') return null;
  const envelope = payload as { data?: unknown; _id?: unknown };
  if (envelope.data && typeof envelope.data === 'object' && typeof (envelope.data as { _id?: unknown })._id === 'string') {
    return envelope.data as RnUzurkuvanjeThread;
  }
  if (typeof envelope._id === 'string') {
    return payload as RnUzurkuvanjeThread;
  }
  return null;
};

export const rnUzurkuvanjeUserLabel = (user?: RnUzurkuvanjeUserRef | string | null): string => {
  if (!user || typeof user === 'string') return '';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return fullName || user.userName || user.email || '';
};

export const rnUzurkuvanjeUserId = (user?: RnUzurkuvanjeUserRef | string | null): string => {
  if (!user) return '';
  return typeof user === 'string' ? user : user._id;
};
